import {
  type PlaybookRunFragment,
  type PlaybookRunsChangedSubscriptionVariables,
  usePlaybookRunsChangedSubscription,
  usePlaybookRunsForConversationQuery,
  usePlaybookRunsQuery,
  useToolInvocationsChangedSubscription,
} from "@qw/graphql";
import { Badge, Button, Collapsible, CollapsibleContent, CollapsibleTrigger, Icon, Spinner } from "@qw/ui";
import React, { useCallback, useContext } from "react";

import { ChatSessionContext } from "../providers/chat-session-provider";

interface PlaybookRunsProps {
  /**
   * The chat to anchor to when this renders outside a `ChatSessionProvider` —
   * the inbox opens run history from the Alby menu, which lives in the nav and
   * so has no `ChatSessionContext` to read the foreground chat off.
   */
  chatSessionId?: string;
  conversationId?: string;
  onRunClick?: (runId: string) => void;
}

interface PlaybookRunProps {
  allRuns: PlaybookRunFragment[];
  onRunClick?: (runId: string) => void;
  onScrollToRun: (runId: string) => void;
  playbookRun: PlaybookRunFragment;
  refetch: () => void;
}

const PlaybookRun = ({ allRuns, onRunClick, onScrollToRun, playbookRun, refetch }: PlaybookRunProps) => {
  const [open, setOpen] = React.useState(false);

  useToolInvocationsChangedSubscription({
    onData: () => {
      refetch();
    },
    variables: { playbookRunId: playbookRun.id },
  });

  const parentRun = allRuns.find((run) => run.id === playbookRun.parentPlaybookRunId);

  return (
    <Collapsible
      className="bg-background"
      key={playbookRun.id}
      open={open}
      onOpenChange={setOpen}
      data-playbook-run-id={playbookRun.id}
    >
      <div className="rounded-md border p-3">
        <div className="flex items-center justify-between">
          <div className="font-medium text-base">{playbookRun.playbook?.name ?? "Playbook"}</div>
          {playbookRun.status === "error" ? (
            <Badge variant="destructive" className="ml-auto">
              Failed
            </Badge>
          ) : playbookRun.status === "cancelled" ? (
            <Badge variant="outline" className="bg-muted text-muted-foreground border-muted-foreground/20 ml-auto">
              <Icon icon="cancel" className="w-2 h-2 mr-2" /> Cancelled
            </Badge>
          ) : playbookRun.status === "running" ? (
            <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 ml-auto">
              <Spinner className="w-2 h-2 mr-2" /> Running
            </Badge>
          ) : playbookRun.status === "awaiting_user_input" ? (
            <Badge variant="outline" className="bg-blue/10 text-blue border-blue/20 ml-auto">
              <Icon icon="pause_circle" className="w-2 h-2 mr-2" /> Awaiting Input
            </Badge>
          ) : playbookRun.status === "awaiting_child_playbook" ? (
            <Badge variant="outline" className="bg-purple/10 text-purple border-purple/20 ml-auto">
              <Icon icon="hourglass_empty" className="w-2 h-2 mr-2" /> Awaiting Child
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-success/10 text-success border-success/20 ml-auto">
              Complete
            </Badge>
          )}
          <CollapsibleTrigger className="ml-2">
            <Button variant="ghost" size="xs">
              {open ? "Hide Details" : "Show Details"}
              <Icon icon={open ? "keyboard_arrow_up" : "keyboard_arrow_down"} className="text-muted-foreground" />
            </Button>
          </CollapsibleTrigger>
        </div>
        {playbookRun.errorMessage ? (
          <div className="text-xs text-destructive mt-1">{playbookRun.errorMessage}</div>
        ) : null}
        <CollapsibleContent asChild>
          <div className="mt-2 space-y-3">
            {parentRun && (
              <div className="border-t pt-2">
                <div className="text-xs font-medium text-muted-foreground mb-1">Nested Execution</div>
                <div className="flex items-center gap-2">
                  <Icon
                    icon="subdirectory_arrow_right"
                    className="text-muted-foreground"
                    style={{ fontSize: "14px" }}
                  />
                  <span className="text-xs">
                    Child of{" "}
                    <button
                      onClick={() => onScrollToRun(parentRun.id)}
                      className="text-primary hover:underline font-medium cursor-pointer"
                    >
                      {parentRun.playbook?.name ?? "Playbook"}
                    </button>
                  </span>
                  <span className="text-xs text-muted-foreground">(depth: {playbookRun.depth})</span>
                </div>
              </div>
            )}

            <div className={parentRun ? "border-t pt-2" : ""}>
              <div className="text-xs font-medium text-muted-foreground mb-1">Tool Invocations</div>
              {playbookRun.toolInvocations.map((invocation) => (
                <div className="flex items-center gap-2 py-1" key={invocation.id}>
                  <Icon
                    icon={invocation.status === "error" ? "error" : "check_circle"}
                    className={invocation.status === "error" ? "text-destructive" : "text-success"}
                  />
                  <h3 className="text-sm font-medium gap-2 truncate">{invocation.toolName}</h3>
                  <span className="text-muted-foreground text-xs ml-auto">
                    {new Date(invocation.calledAt).toLocaleString()}
                  </span>
                </div>
              ))}
              {playbookRun.toolInvocations.length === 0 ? (
                <div className="text-xs text-muted-foreground">No tool calls recorded.</div>
              ) : null}
            </div>

            {onRunClick && (
              <div className="border-t pt-2">
                <button
                  type="button"
                  onClick={() => onRunClick(playbookRun.id)}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Icon icon="open_in_new" style={{ fontSize: "14px" }} />
                  View full run details
                </button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const PlaybookRuns = ({ chatSessionId, conversationId, onRunClick }: PlaybookRunsProps) => {
  const chatSessionContext = useContext(ChatSessionContext);
  const anchorChatSessionId = chatSessionId ?? chatSessionContext?.chatSession?.id;

  // Conversation pages query playbook_run.conversationId directly so runs
  // are found regardless of how chat sessions are partitioned. Insured and
  // foreground-chat surfaces stay session-scoped (the ChatSessionContext
  // session IS the anchor there); those paths fall through to the existing
  // playbookRuns(chatSessionId) query.
  const activeAnchor: "conversation" | "session" | "none" = conversationId
    ? "conversation"
    : anchorChatSessionId
      ? "session"
      : "none";

  const conversationTree = usePlaybookRunsForConversationQuery({
    variables: { conversationId: conversationId ?? "" },
    skip: activeAnchor !== "conversation",
  });
  const sessionTree = usePlaybookRunsQuery({
    variables: { chatSessionId: anchorChatSessionId },
    skip: activeAnchor !== "session",
  });

  // Key dataset/loading/refetch off the active anchor so stale cache data
  // from a previously-active anchor can't render after the anchor switches.
  const playbookRuns: PlaybookRunFragment[] =
    (activeAnchor === "conversation"
      ? conversationTree.data?.playbookRunsForConversation
      : activeAnchor === "session"
        ? sessionTree.data?.playbookRuns
        : undefined) ?? [];
  const isLoading =
    activeAnchor === "conversation"
      ? conversationTree.loading
      : activeAnchor === "session"
        ? sessionTree.loading
        : false;
  const refetch = useCallback(() => {
    if (activeAnchor === "conversation") {
      void conversationTree.refetch();
    } else if (activeAnchor === "session") {
      void sessionTree.refetch();
    }
  }, [activeAnchor, conversationTree.refetch, sessionTree.refetch]);

  const subscriptionVariables: PlaybookRunsChangedSubscriptionVariables | null =
    activeAnchor === "conversation"
      ? { conversationId }
      : activeAnchor === "session"
        ? { chatSessionId: anchorChatSessionId }
        : null;
  usePlaybookRunsChangedSubscription({
    onData: () => {
      refetch();
    },
    variables: subscriptionVariables ?? {},
    skip: subscriptionVariables === null,
  });

  const scrollToPlaybookRun = React.useCallback((playbookRunId: string) => {
    const element = document.querySelector(`[data-playbook-run-id="${playbookRunId}"]`);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.classList.add("ring-2", "ring-primary", "ring-offset-2");
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-primary", "ring-offset-2");
    }, 2000);
  }, []);

  if (isLoading) {
    return (
      <div>
        <h5 className="font-medium mb-2">Playbook History</h5>
        <div className="flex items-center gap-2">
          <Spinner className="w-4 h-4" />
          <p className="text-xs text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (activeAnchor === "none") {
    return (
      <div>
        <h5 className="font-medium mb-2">Playbook History</h5>
        <p className="text-xs text-muted-foreground">No playbook runs found for this chat session.</p>
      </div>
    );
  }

  return (
    <div>
      <h5 className="font-medium mb-2">Playbook History</h5>
      {playbookRuns?.length ? (
        <>
          <p className="text-xs text-muted-foreground mb-2">
            The following playbooks have been run in this chat session.
          </p>
          <div className="space-y-3">
            {playbookRuns.map((run) => (
              <PlaybookRun
                key={run.id}
                allRuns={playbookRuns}
                onRunClick={onRunClick}
                onScrollToRun={scrollToPlaybookRun}
                playbookRun={run}
                refetch={refetch}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">No playbook runs found for this chat session.</p>
      )}
    </div>
  );
};
